import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { Panel, RelatedPerson, useSession } from './App';
import { name, type Row, type Schema } from './api/client';
import { allRows, useAccess, useEmployee, useRows, useCompleteRows } from './api/data';
import { EmployeeRecords, EmployeeRoleProfile } from './employee-records';
import { DomainPage } from './hr-pages';
import { findResource } from './modules';
import { employeeStatusLabels, EmployeeHistory } from './employee-history';
import { human } from './forms';
import {
  employeeAccessTargets,
  employeeStatusLabel,
  employeeStatusMatches,
} from './hr-permissions';
import { actionOperation } from './resource-routes';
import {
  Select,
  Avatar,
  Button,
  Empty,
  ErrorState,
  Icon,
  IconButton,
  Loading,
  PersonCell,
  Search,
  StatTabs,
  Table,
  Tabs,
  type Column,
} from './ui';

function useDirectory() {
  const access = useAccess();
  const employees = useCompleteRows<Schema<'EmployeeResponse'>>(
    '/api/v1/employees',
    access.can('employees.read'),
  );
  const departments = useCompleteRows('/api/v1/departments', access.can('departments.read'));
  const teams = useCompleteRows('/api/v1/teams', access.can('projects.read'));
  const roles = useCompleteRows(
    '/api/v1/positions' as import('./api/client').GetPath,
    access.can('positions.read'),
  );
  const seniorities = useCompleteRows('/api/v1/seniorities', access.can('seniorities.read'));
  const locations = useCompleteRows('/api/v1/locations', access.can('locations.read'));
  return { employees, departments, teams, roles, seniorities, locations };
}

function labelled(rows: Row[], id: string | undefined) {
  return name(rows.find((row) => row.id === id)) || '-';
}
function date(value: string) {
  return value
    ? new Date(`${value.slice(0, 10)}T12:00:00`).toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : '-';
}

export function PeoplePage() {
  const access = useAccess();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('ACTIVE');
  const [external, setExternal] = useState(false);
  const [reports, setReports] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const navigate = useNavigate();
  const session = useSession();
  const directory = useDirectory();
  const population = useQuery({
    queryKey: ['employee-population'],
    enabled: access.can('employees.read'),
    queryFn: ({ signal }) => allRows('/api/v1/employees', {}, signal),
    staleTime: 15_000,
  });
  if (access.isLoading) return <Loading />;
  if (!access.can('employees.read'))
    return (
      <Empty
        title="Нет доступа к сотрудникам"
        subtitle="У вашей учётной записи нет доступа к этому разделу."
      />
    );
  const matches = (person: Row) =>
    (external || !person.externalEmployee) &&
    (!reports || person.lineManagerId === session.account?.employeeId) &&
    (!search ||
      [person.workName, person.jobTitle, person.workEmail].some((value) =>
        String(value || '')
          .toLocaleLowerCase('ru')
          .includes(search.toLocaleLowerCase('ru')),
      ));
  const inBucket = (person: Row, value: string) =>
    employeeStatusMatches(String(person.status), value);
  const rows = (population.data || []).filter(
    (person) => inBucket(person, status) && matches(person),
  ) as Schema<'EmployeeResponse'>[];
  const count = (value: string) =>
    population.data
      ? population.data.filter((person) => inBucket(person, value) && matches(person)).length
      : '…';
  const canEdit = access.can('employees.edit');
  const columns: Column<Schema<'EmployeeResponse'>>[] = [
    ...(canEdit
      ? [
          {
            key: 'selection',
            label: '',
            width: 48,
            sortable: false,
            filter: false,
            render: (person: Schema<'EmployeeResponse'>) => (
              <input
                type="checkbox"
                aria-label={`Выбрать ${person.workName}`}
                disabled={
                  !access.can('employees.edit', employeeAccessTargets(person, session.account))
                }
                checked={selected.includes(person.id)}
                onClick={(event) => event.stopPropagation()}
                onChange={(event) =>
                  setSelected((previous) =>
                    event.target.checked
                      ? [...previous, person.id]
                      : previous.filter((id) => id !== person.id),
                  )
                }
              />
            ),
          },
        ]
      : []),
    {
      key: 'workName',
      label: 'Имя',
      width: 238,
      render: (person) => <PersonCell person={person} />,
    },
    {
      key: 'departmentId',
      label: 'Отдел',
      width: 260,
      render: (person) =>
        String(person.departmentName || labelled(directory.departments.rows, person.departmentId)),
    },
    {
      key: 'primaryTeamId',
      label: 'Проект',
      width: 220,
      render: (person) =>
        String(
          person.primaryTeamName ||
            labelled(directory.teams.rows, person.primaryTeamId || undefined),
        ),
    },
    { key: 'jobTitle', label: 'Должность', width: 236 },
    {
      key: 'seniorityId',
      label: 'Уровень',
      width: 130,
      render: (person) => (
        <span className="seniority">
          <Icon name="People" size={12} />
          {labelled(directory.seniorities.rows, person.seniorityId)}
        </span>
      ),
    },
    {
      key: 'lineManagerId',
      label: 'Руководитель',
      width: 236,
      render: (person) =>
        directory.employees.rows.some((row) => row.id === person.lineManagerId) ? (
          <RelatedPerson people={directory.employees.rows} id={person.lineManagerId} />
        ) : (
          <span>{String(person.lineManagerName || 'Не назначен')}</span>
        ),
    },
    {
      key: 'status',
      label: 'Статус',
      width: 144,
      render: (person) => (
        <span
          className={`status status-${person.status === 'OFFBOARDING' ? 'active' : person.status.toLowerCase()}`}
        >
          {employeeStatusLabel(person.status) || human(person.status)}
        </span>
      ),
    },
  ];
  return (
    <>
      <section className="panel data-panel people-panel">
        <StatTabs
          tabs={[
            { value: 'all', label: 'Все', count: count('all') },
            ...Object.entries(employeeStatusLabels).map(([value, label]) => ({
              value,
              label,
              count: count(value),
            })),
          ]}
          selected={status}
          onChange={setStatus}
        />
        <div className="table-toolbar">
          <Search value={search} onChange={setSearch} placeholder="Поиск по имени или должности" />
          <Button className={external ? 'selected' : ''} onClick={() => setExternal(!external)}>
            Включая внешних
          </Button>
          <Button
            className={reports ? 'selected' : ''}
            disabled={!session.account?.employeeId}
            onClick={() => setReports(!reports)}
          >
            Мои подчинённые
          </Button>
          <span className="toolbar-spacer" />
          {access.can('employees.create') && (
            <Button
              icon="Plus"
              onClick={() =>
                void navigate({
                  to: '/$section/$resource/new',
                  params: { section: 'people', resource: 'employees' },
                  search: { employeeId: undefined },
                })
              }
            >
              Добавить сотрудника
            </Button>
          )}
          {access.can('employees.import') && (
            <Button onClick={() => void navigate({ to: '/people/employees/import' })}>
              Импорт сотрудников
            </Button>
          )}
          <Button icon="People" onClick={() => void navigate({ to: '/organisation/chart' })}>
            Оргструктура
          </Button>
        </div>
        {canEdit && selected.length > 0 && (
          <div className="employee-bulk-toolbar">
            <strong>Выбрано: {selected.length}</strong>
            <Button
              onClick={() =>
                void navigate({
                  to: '/people/employees/bulk',
                  search: {
                    employees: JSON.stringify(
                      (population.data || [])
                        .filter((person) => selected.includes(person.id))
                        .map((person) => ({ id: person.id, version: person.version })),
                    ),
                  },
                })
              }
            >
              Изменить выбранных
            </Button>
            <Button onClick={() => setSelected([])}>Снять выбор</Button>
          </div>
        )}
        {population.error ? (
          <ErrorState
            error={population.error}
            retry={() => {
              void population.refetch();
            }}
          />
        ) : (
          <Table
            rows={rows}
            columns={columns}
            empty="Здесь появятся сотрудники"
            loading={population.isLoading}
            onRow={(person) => {
              void navigate({
                to: '/employees/profile/$employeeId',
                params: { employeeId: person.id },
              });
            }}
          />
        )}
      </section>
    </>
  );
}

export function ProfilePage() {
  const session = useSession();
  const location = useRouterState({ select: (state) => state.location });
  const path = location.pathname;
  const navigate = useNavigate();
  const id =
    path === '/my-profile' ? session.account?.employeeId || undefined : path.split('/').at(-1);
  const employee = useEmployee(id);
  const directory = useDirectory();
  const tab = (location.search as { tab?: string }).tab || 'profile';
  const setTab = (value: string) =>
    void navigate({
      to: '/employees/profile/$employeeId',
      params: { employeeId: id! },
      search: { tab: value },
    });
  const edit = () =>
    void navigate({
      to: '/employees/profile/$employeeId/$operation',
      params: { employeeId: id!, operation: 'edit' },
    });
  const access = useAccess();
  if (!id)
    return (
      <section className="panel">
        <Empty
          icon="NationalId"
          title="Профиль сотрудника ещё не привязан"
          subtitle="Попросите HR связать вашу учётную запись с профилем сотрудника."
        />
      </section>
    );
  if (employee.isLoading) return <Loading />;
  if (employee.error || !employee.data)
    return (
      <ErrorState
        error={employee.error}
        retry={() => {
          void employee.refetch();
        }}
      />
    );
  const person = employee.data;
  const target = employeeAccessTargets(person, session.account);
  const canEdit = access.can('employees.edit', target);
  const details = [
    ['Рабочая почта', person.workEmail],
    ['Рабочий Telegram', String(person.workTelegram || '—')],
    ['Личный Telegram', String(person.personalTelegram || '—')],
    [
      'Отдел',
      String(person.departmentName || labelled(directory.departments.rows, person.departmentId)),
    ],
    [
      'Основной проект',
      String(
        person.primaryTeamName || labelled(directory.teams.rows, person.primaryTeamId || undefined),
      ),
    ],
    [
      'Дополнительные проекты',
      Array.isArray(person.additionalTeamNames) && person.additionalTeamNames.length
        ? person.additionalTeamNames.join(', ')
        : (person.additionalTeamIds || [])
            .map((id) => labelled(directory.teams.rows, id))
            .join(', ') || '—',
    ],
    ['Должность', person.jobTitle],
    [
      'Уровень',
      String(person.seniorityName || labelled(directory.seniorities.rows, person.seniorityId)),
    ],
    [
      'Руководитель',
      String(
        person.lineManagerName ||
          labelled(directory.employees.rows, person.lineManagerId || undefined),
      ),
    ],
    [
      'Локация',
      String(person.locationName || labelled(directory.locations.rows, person.locationId)),
    ],
    ['Дата выхода', date(person.startDate)],
    ['Тип занятости', human(person.employmentType)],
    ['Формат работы', human(String(person.workFormat || 'REMOTE'))],
    ['Статус', employeeStatusLabel(person.status) || human(person.status)],
  ];
  return (
    <>
      <section className="panel profile-header">
        <Avatar label={person.workName} size={48} />
        <div>
          <h2>{person.workName}</h2>
          <div className="profile-meta">
            <span
              className={`status status-${person.status === 'OFFBOARDING' ? 'active' : person.status.toLowerCase()}`}
            >
              {employeeStatusLabel(person.status) || human(person.status)}
            </span>
            <span>
              <Icon name="People" size={16} />
              {person.jobTitle}
            </span>
            <span>
              <Icon name="People" size={16} />
              {String(
                person.primaryTeamName ||
                  labelled(directory.teams.rows, person.primaryTeamId || undefined),
              )}
            </span>
            <span>{labelled(directory.locations.rows, person.locationId)}</span>
          </div>
        </div>
        <div className="profile-actions">
          {canEdit && (
            <Button icon="Pencil" onClick={edit}>
              Изменить
            </Button>
          )}
          {findResource('people', 'employees')
            ?.actions?.(person)
            .filter((action) => !action.permission || access.can(action.permission, target))
            .map((action) => (
              <Button
                key={action.label}
                onClick={() =>
                  void navigate({
                    to: '/employees/profile/$employeeId/$operation',
                    params: { employeeId: id!, operation: actionOperation(action) },
                  })
                }
              >
                {action.label}
              </Button>
            ))}
        </div>
      </section>
      <Tabs
        value={tab}
        onChange={setTab}
        tabs={[
          { label: 'Профиль', value: 'profile', icon: 'NationalId' },
          { label: 'История изменений', value: 'history', icon: 'DocumentsPair' },
          ...(access.can('performance_reviews.read')
            ? [{ label: 'Эффективность', value: 'performance', icon: 'StarFilled' }]
            : []),
          ...(access.can('work_plans.read')
            ? [{ label: 'Рабочие планы', value: 'goals', icon: 'TargetDarts' }]
            : []),
          ...(access.can('employee_fields.documents.read')
            ? [{ label: 'Документы', value: 'documents', icon: 'DocumentsPair' }]
            : []),
          ...(access.can('employee_fields.compensation.read')
            ? [{ label: 'Вознаграждение', value: 'compensation', icon: 'Bank' }]
            : []),
          ...(access.can('employee_fields.screening.read')
            ? [
                { label: 'Проверки', value: 'screenings', icon: 'DocumentChecked' },
                { label: 'Право на работу', value: 'right-to-work', icon: 'NationalId' },
              ]
            : []),
          ...(access.can('employee_fields.payroll.read')
            ? [{ label: 'Расчёт зарплаты', value: 'payroll', icon: 'Bank' }]
            : []),
        ]}
      />
      {tab === 'profile' ? (
        <div className="profile-grid">
          <div>
            <Panel
              title="Рабочие данные"
              action={
                canEdit ? (
                  <IconButton icon="Pencil" label="Изменить рабочие данные" onClick={edit} />
                ) : undefined
              }
            >
              <div className="details-list">
                {details.map(([label, value]) => (
                  <div key={label}>
                    <span>{label}</span>
                    <span>{value}</span>
                  </div>
                ))}
              </div>
              {canEdit && (
                <Button className="see-all" onClick={edit}>
                  Подробнее
                </Button>
              )}
            </Panel>
            <PersonalDetails employeeId={id} />
            {access.can('employee_fields.screening.read') && (
              <Panel title="Право на работу">
                <EmployeeRecords employeeId={id} kind="right-to-work" />
              </Panel>
            )}
          </div>
          <div>
            <Panel title="Оргструктура">
              <div className="org-chart">
                <div>
                  {directory.employees.rows.some((row) => row.id === person.lineManagerId) ? (
                    <RelatedPerson people={directory.employees.rows} id={person.lineManagerId} />
                  ) : (
                    <span>{String(person.lineManagerName || 'Не назначен')}</span>
                  )}
                  <Icon name="ArrowDown" />
                  <Avatar label={person.workName} size={48} />
                  <strong>{person.workName}</strong>
                  <span>{person.jobTitle}</span>
                </div>
              </div>
            </Panel>
            <EmployeeRoleProfile employee={person} />
            <Panel title="Ответственность">
              <Link
                className="panel-link"
                to="/organisation/teams/departments"
                search={{ organizationId: undefined }}
              >
                Открыть организацию
                <Icon name="ChevronRight" />
              </Link>
            </Panel>
          </div>
        </div>
      ) : tab === 'history' ? (
        <EmployeeHistory employeeId={id} />
      ) : ['documents', 'compensation', 'screenings', 'right-to-work', 'payroll'].includes(tab) ? (
        <section className="panel data-panel">
          <EmployeeRecords
            employeeId={id}
            kind={tab as 'documents' | 'compensation' | 'screenings' | 'right-to-work' | 'payroll'}
          />
        </section>
      ) : tab === 'performance' ? (
        <ReviewsPage employeeId={id} />
      ) : tab === 'goals' || tab === 'roadmap' ? (
        <GoalsPage employeeId={id} />
      ) : (
        <EmployeeRecords employeeId={id} kind="screenings" />
      )}
    </>
  );
}

function PersonalDetails({ employeeId }: { employeeId: string }) {
  const access = useAccess();
  return (
    <Panel title="Личные данные">
      {access.can('employee_fields.personal.read') ? (
        <Link
          className="see-all pill"
          to="/employees/profile/$employeeId/$operation"
          params={{ employeeId, operation: 'personal-details' }}
        >
          Подробнее
        </Link>
      ) : (
        <Empty title="Нет доступа к личным данным" />
      )}
    </Panel>
  );
}

export function GoalsPage({ employeeId }: { employeeId?: string }) {
  return <DomainPage section="performance" resource="work-plans" employeeId={employeeId} />;
}
export function ReviewsPage({ employeeId }: { employeeId?: string }) {
  return <DomainPage section="performance" resource="reviews" employeeId={employeeId} />;
}

export function DocumentsPage() {
  const session = useSession();
  const employees = useRows('/api/v1/employees');
  const [id, setId] = useState(session.account?.employeeId || '');
  return (
    <>
      <div className="employee-scope">
        <label>
          Сотрудник
          <Select value={id} onChange={(event) => setId(event.target.value)}>
            <option value="">Выберите сотрудника</option>
            {employees.rows.map((row) => (
              <option key={row.id} value={row.id}>
                {name(row)}
              </option>
            ))}
          </Select>
        </label>
      </div>
      <section className="panel data-panel">
        {id ? (
          <EmployeeRecords employeeId={id} kind="documents" />
        ) : (
          <Empty icon="DocumentsPair" title="Выберите сотрудника для просмотра документов" />
        )}
      </section>
    </>
  );
}

export function OrganisationChartPage() {
  const directory = useDirectory();
  const employees = directory.employees.rows.filter(
    (person) => !['INACTIVE', 'ARCHIVED'].includes(person.status),
  );
  const ids = new Set(employees.map((person) => person.id));
  const roots = employees.filter(
    (person) => !person.lineManagerId || !ids.has(person.lineManagerId),
  );
  const reached = new Set<string>();
  const branch = (person: Schema<'EmployeeResponse'>, ancestors: Set<string>): React.ReactNode => {
    if (ancestors.has(person.id)) return null;
    reached.add(person.id);
    const next = new Set([...ancestors, person.id]);
    const reports = employees.filter(
      (report) => report.lineManagerId === person.id && !next.has(report.id),
    );
    return (
      <div className="org-branch" key={person.id}>
        <Link
          className="org-person-card"
          to="/employees/profile/$employeeId"
          params={{ employeeId: person.id }}
        >
          <Avatar label={person.workName} size={48} />
          <strong>{person.workName}</strong>
          <span className="muted">{person.jobTitle}</span>
          <span className="muted">
            {String(
              person.primaryTeamName ||
                labelled(directory.teams.rows, person.primaryTeamId || undefined),
            )}
          </span>
        </Link>
        {reports.length > 0 && (
          <>
            <Icon name="ArrowDown" />
            <div className="org-reports">{reports.map((report) => branch(report, next))}</div>
          </>
        )}
      </div>
    );
  };
  const trees = roots.map((person) => branch(person, new Set()));
  const remaining = employees.filter((person) => !reached.has(person.id));
  return (
    <section className="panel">
      <header>
        <h2>Оргструктура</h2>
      </header>
      {directory.employees.error ? (
        <ErrorState error={directory.employees.error} />
      ) : directory.employees.isLoading ? (
        <Loading />
      ) : (
        <div className="org-hierarchy">
          <div className="org-reports">
            {trees}
            {remaining.map((person) => branch(person, new Set()))}
          </div>
          {!employees.length && <Empty title="Сотрудников пока нет" />}
        </div>
      )}
    </section>
  );
}
